import createCrudModule from 'vuex-crud'
import api from 'utils/api'

// Custom URL function for project-scoped submission endpoints
const customUrlFn = (id, params) => {
  const base = `/projects/${params.project_id}/submissions`
  return id ? `${base}/${id}` : base
}

// Additional state beyond vuex-crud defaults
const state = {
  meta: {},
  accountList: [],          // Account-wide submissions (separate from project-scoped)
  isFetchingAccountList: false,
  isApproving: false,
  isRejecting: false,
  isAmending: false,
  isSaving: false,          // Save-only (update without approval)
  auditTrail: [],           // Audit trail entries for current submission
  isFetchingAuditTrail: false
}

// Custom actions for account-wide list and review operations
const actions = {
  // Fetch account-wide pending submissions: GET /api/submissions
  // Can be filtered by status and/or worker_id
  fetchAccountList({ commit }, { status, worker_id, page } = {}) {
    commit('fetchAccountListStart')
    let params = { filter: { status: status || 'submitted' }, page }
    if (worker_id) params.filter.worker_id = worker_id

    return api.client.get('/submissions', { params })
      .then(res => {
        commit('fetchAccountListSuccess', res.data)
        return Promise.resolve(res.data)
      })
      .catch(err => {
        commit('fetchAccountListError')
        return Promise.reject(err.response ? err.response.data : err)
      })
  },

  // Approve submission as-is: POST /api/projects/:project_id/submissions/:id/approve
  approve({ commit }, { projectId, id }) {
    const url = `/projects/${projectId}/submissions/${id}/approve`
    commit('approveStart')

    return api.client.post(url)
      .then(res => {
        commit('approveSuccess', res.data)
        return Promise.resolve(res.data)
      })
      .catch(err => {
        commit('approveError')
        return Promise.reject(err.response ? err.response.data : err)
      })
  },

  // Reject submission with notes: POST /api/projects/:project_id/submissions/:id/reject
  reject({ commit }, { projectId, id, notes, sendEmail }) {
    const url = `/projects/${projectId}/submissions/${id}/reject`
    commit('rejectStart')

    return api.client.post(url, { notes, send_email: sendEmail })
      .then(res => {
        commit('rejectSuccess', res.data)
        return Promise.resolve(res.data)
      })
      .catch(err => {
        commit('rejectError')
        return Promise.reject(err.response ? err.response.data : err)
      })
  },

  // Amend submission (modify & approve): POST /api/projects/:project_id/submissions/:id/amend
  amend({ commit }, { projectId, id, tasks }) {
    const url = `/projects/${projectId}/submissions/${id}/amend`
    commit('amendStart')

    return api.client.post(url, { tasks })
      .then(res => {
        commit('amendSuccess', res.data)
        return Promise.resolve(res.data)
      })
      .catch(err => {
        commit('amendError')
        return Promise.reject(err.response ? err.response.data : err)
      })
  },

  // Update submission (save without approval): PATCH /api/projects/:project_id/submissions/:id
  updateSubmission({ commit }, { projectId, id, tasks, notes }) {
    const url = `/projects/${projectId}/submissions/${id}`
    commit('saveStart')

    return api.client.patch(url, { submission: { tasks, notes } })
      .then(res => {
        commit('saveSuccess', res.data)
        return Promise.resolve(res.data)
      })
      .catch(err => {
        commit('saveError')
        return Promise.reject(err.response ? err.response.data : err)
      })
  },

  // Fetch audit trail: GET /api/projects/:project_id/submissions/:id/audit_trail
  fetchAuditTrail({ commit }, { projectId, id }) {
    const url = `/projects/${projectId}/submissions/${id}/audit_trail`
    commit('fetchAuditTrailStart')

    return api.client.get(url)
      .then(res => {
        commit('fetchAuditTrailSuccess', res.data)
        return Promise.resolve(res.data)
      })
      .catch(err => {
        commit('fetchAuditTrailError')
        return Promise.reject(err.response ? err.response.data : err)
      })
  }
}

// Custom mutations for loading states and account-wide list
const mutations = {
  // Account list mutations
  fetchAccountListStart(state) {
    state.isFetchingAccountList = true
  },
  fetchAccountListSuccess(state, data) {
    state.isFetchingAccountList = false
    state.accountList = data.data || data
    if (data.meta) state.meta = data.meta
  },
  fetchAccountListError(state) {
    state.isFetchingAccountList = false
  },

  // Approve mutations
  approveStart(state) {
    state.isApproving = true
  },
  approveSuccess(state) {
    state.isApproving = false
  },
  approveError(state) {
    state.isApproving = false
  },

  // Reject mutations
  rejectStart(state) {
    state.isRejecting = true
  },
  rejectSuccess(state) {
    state.isRejecting = false
  },
  rejectError(state) {
    state.isRejecting = false
  },

  // Amend mutations
  amendStart(state) {
    state.isAmending = true
  },
  amendSuccess(state) {
    state.isAmending = false
  },
  amendError(state) {
    state.isAmending = false
  },

  // Save (update without approval) mutations
  saveStart(state) {
    state.isSaving = true
  },
  saveSuccess(state) {
    state.isSaving = false
  },
  saveError(state) {
    state.isSaving = false
  },

  // Audit trail mutations
  fetchAuditTrailStart(state) {
    state.isFetchingAuditTrail = true
    state.auditTrail = []
  },
  fetchAuditTrailSuccess(state, data) {
    state.isFetchingAuditTrail = false
    state.auditTrail = data.data || []
  },
  fetchAuditTrailError(state) {
    state.isFetchingAuditTrail = false
    state.auditTrail = []
  }
}

// Custom getters
const getters = {
  // Get account-wide submission list
  accountList: state => state.accountList,

  // Check if any review action is in progress
  isReviewInProgress: state => state.isApproving || state.isRejecting || state.isAmending || state.isSaving,

  // Get audit trail entries
  auditTrail: state => state.auditTrail
}

export default createCrudModule({
  client: api.client,
  resource: 'submissions',
  parseList: res => res.data,
  parseSingle: res => res.data,
  onFetchListSuccess: (state, { meta }) => {
    if (meta) state.meta = meta
  },
  customUrlFn,
  state,
  actions,
  mutations,
  getters
})